import { SpriteLabRequest, SpriteLabState } from "game-common/spritelab"
import { ContentParticipant } from "game-common/content/publishing"
import { contentRuntime, contentRevision, requireContentRevision } from "game-common/content/catalog"
import { ApplyInputResultPool } from "game-common/apply_input_result_pool"
import { InventoryAction } from "game-common/item/item"
import { MapDelta } from "game-common/map/map"
import {
    Appearance,
    BiomeMapPoint,
    BuildingPointOptions,
    Coordinate,
    Entity,
    EmberPlayMode,
    EntityId,
    getCoordinateDistance,
    Inventory,
    Latch,
    LocationIcon,
    Message,
    MessageEnvelope,
    Movement,
    PlayerEvent,
    SIGIL_FLAG,
    SubmittedFeedback,
    TileSize,
    WorldState,
} from "game-common/models"
import { Protocol } from "game-common/protocol"

import { ClientGameLogic } from "./client_game_logic"
import { KeyModifiers } from "./client_models"
import { randomId } from "game-common/util"
import { Mechanics } from "game-common/mechanics/mechanics"
import { logger } from "./logger"
import { resolveConfig } from "./runtime_config"
import { isMobile } from "./client_util"
import { hideDisconnected, showDisconnected, showSessionEnded } from "./disconnected_overlay"
import { AgentController } from "./agent/agent_controller"
import { enterFullscreenOnFirstGesture } from "./fullscreen"
import { installNetworkConditioner } from "./network_conditioner"
import { ConnectionLifecycle } from "./connection_lifecycle"

export class Client {
    private spriteLabRoomId?: string
    private spriteLabVisible = false
    private spriteLabPollAt = 0

    // Whether a socket has ever opened this session. Only used to word the disconnected panel:
    // "connection lost" and "can't reach the server" are different situations to be in, and the
    // second one usually means the server rather than the player's wifi.
    private everConnected = false
    private last_ts?: number
    private lastState: boolean | undefined = undefined
    private sendState: boolean
    private primingLatch: Latch
    private incompleteEntityLatch: Latch = new Latch(2000)
    private lastMovement: Movement

    // Reconciliation error-smoothing for the local player. When an authoritative
    // update disagrees with the client's predicted position (e.g. the server has
    // slowed the player during a push that the client predicted at full speed),
    // we don't snap the rendered position to the correction -- that reads as a
    // shudder because the camera follows the player. Instead we keep the
    // authoritative/predicted position as truth in entity.location and carry the
    // visual delta here, decaying it toward zero over a few frames so the camera
    // eases into the correction. Large deltas (teleports/room changes) bypass this.
    private playerPositionError: Coordinate = { x: 0, y: 0 }
    private static readonly POSITION_ERROR_DECAY = 0.8 // per-frame; ~200ms ease at 60fps
    private static readonly POSITION_ERROR_SNAP_EPSILON = 0.05 // px; below this, clear the error

    // Offset from the server's clock to ours, in ms: add it to a server timestamp to get the
    // equivalent reading of this client's Date.now(). Estimated as the MINIMUM of
    // (arrival - sent) over recent frames, because the least-delayed frame is the one whose
    // travel time least contaminates the offset; anything above that minimum is queueing, not
    // clock difference. It then creeps back up very slowly so real clock drift is followed
    // without a single fast frame pinning the estimate forever.
    private serverClockOffset?: number
    private static readonly SERVER_CLOCK_CREEP = 0.0005

    // How far behind the server's clock other entities are drawn. The buffer has to hold at
    // least one sample older than this for interpolation to have something to work with, so it
    // must cover the one-way latency plus the gap between updates for that entity.
    private static readonly INTERPOLATION_DELAY_MS = 150

    // Interpolation can only ever be as even as the samples it is fed, and the samples are not
    // even: the server advances a player when it dequeues one of that player's input frames
    // (~16ms apart) but photographs the world on its own 10ms tick, so consecutive samples are
    // one step of movement over alternately 10ms and 20ms. Replayed literally that is a real
    // ±25% speed ripple at ~12Hz -- a visible shimmer on anything moving. So the interpolated
    // point is a TARGET, and the drawn position chases it through a first-order filter, which
    // attenuates the ripple to a few percent. The cost is TAU more latency on top of the
    // interpolation delay, which nothing about this game is tight enough to notice.
    private renderSmoothing: Map<EntityId, { x: number; y: number; ts: number }> = new Map()
    private static readonly RENDER_SMOOTHING_TAU_MS = 55
    // Past this, a "correction" is a teleport (respawn, room change, coming back into view) and
    // easing into it would draw the entity skating across the map. A tile is far more than the
    // few pixels a filter this short ever trails a moving entity by.
    private static readonly RENDER_SMOOTHING_SNAP_PX_SQ = TileSize * TileSize
    // ...and below this, land on the target exactly instead of approaching it forever. An
    // exponential filter has no finish line: it trails a walking entity by about 4px, and when
    // that entity stops it keeps closing the gap by ever-smaller fractions for ~200 frames before
    // the delta underflows. Every one of those sub-micron steps counts as movement to
    // lpc_sprite.doMovement, which picks walk-vs-idle by comparing the position to last frame's,
    // so a remote player who had stopped kept walking on the spot for the better part of two
    // seconds. Half a screen pixel at surfaceZoom 2, so landing early can never show as a jump.
    private static readonly RENDER_SMOOTHING_SETTLE_PX_SQ = 0.25 * 0.25

    incomingWorldStates: WorldState[] = []
    /**
     * Hard ceiling on the queue above. The drain is `update()`, which runs on the pixi ticker --
     * requestAnimationFrame -- and a BACKGROUNDED tab is given no frames at all, while
     * `ws.onmessage` keeps firing because a hidden tab still receives network events. So this
     * queue grew at the server's tick rate with nothing taking anything off it, until the renderer
     * was killed for its memory and the socket died with it. That is the "websocket dies when the
     * game is backgrounded" report.
     *
     * The real fix is to stop the stream at the source (see ConnectionLifecycle and
     * type_suspendUpdates). This cap is the backstop for the gap that is left: the couple of
     * seconds before the suspend is sent, a server too old to honour it, a resume message that
     * never arrives. Sized at roughly a third of a second of frames -- far more than any drawn
     * frame needs, far less than it takes to matter -- and overflow drops the OLDEST, because the
     * newest frame is the only one worth drawing. Dropping any frame leaves the picture
     * incomplete (the stream is deltas), so it also sets `desynced`, and the resume path turns
     * that into a full resend rather than letting the player wander round a stale world.
     */
    private static readonly MAX_QUEUED_WORLD_STATES = 20
    /** A world-state frame was dropped, so this client's picture has a hole in it. */
    private desynced = false
    /**
     * Rate limit on asking the server to resend the world. A resync is a burst of work for one
     * player on a box that has a tick to make, so it is worth being certain it cannot be asked for
     * repeatedly. (It cannot in practice -- the only thing that drops frames is the drain not
     * running, and the request is made FROM the drain -- but this costs nothing to be sure of.)
     */
    private resyncRequestLatch: Latch = new Latch(5000)
    ws: WebSocket
    /**
     * Every reason to dial again, in one place: the page coming back from the background, a socket
     * that closed, and a socket that is still OPEN but has gone silent (the resumed-WebView corpse,
     * which never fires a close event). Not created in headless mode -- there is no page to hide.
     */
    private lifecycle?: ConnectionLifecycle
    /** Re-run the current connection attempt with the same credentials. Set by connectImpl. */
    private redial?: () => void
    /**
     * Why the server ended this session, once it has (type_sessionEnded) -- "signedInElsewhere" when
     * the account was taken by another login. Set means "do not dial again": the socket is about to
     * close and this is not the kind of close a reconnect answers.
     */
    sessionEndReason?: string

    private contentParticipant?: ContentParticipant

    // Headless/testing transport injection. When set, connectImpl uses this socket instead
    // of constructing a real browser WebSocket, and skips DOM redirects on close. See
    // connectHeadless(). Untouched in the browser (both stay undefined).
    private injectedSocket?: any
    private loginCredentials?: { username: string; password: string }
    /**
     * How often a WALKING player's own position is reconciled against the server's. It was 100ms,
     * which meant a player holding a direction ignored nine out of every ten authoritative
     * positions -- fine while the only thing moving them is their own input, which they predict
     * exactly, but not when the server applies a force they cannot predict. A push is such a force:
     * the gap grew for a whole 100ms before anything was folded in, and a correction that big is
     * over the half-tile "that was a teleport" line in accumulatePlayerPositionError, so it was
     * snapped rather than eased -- 21px in one 16ms frame, against a 1.4px stride. Reconciling once
     * per drawn frame keeps each correction down to a pixel or two, which the smoothing absorbs
     * invisibly, and costs no more than the prediction already running every frame.
     */
    interpolationTs: Latch = new Latch(16)
    interpolatable: Set<EntityId> = new Set()
    clientGameLogic: ClientGameLogic

    // LLM remote-control of the local player, started via the `/agent <goal>` chat command.
    agentController?: AgentController

    private interpolationLogLatch: Latch = new Latch(3000) // Log every 3 seconds
    private interpolationStats = {
        totalInterpolatable: 0,
        culledEntities: 0,
        actuallyInterpolated: 0,
        skippedNoBuffer: 0,
        skippedNotInterpolatable: 0,
        // Frames where the buffer could not bracket the render timestamp, so the entity was
        // drawn holding a sample instead of moving between two. A nonzero rate here means the
        // interpolation delay no longer covers this connection's latency plus update gap.
        underran: 0,
    }

    constructor(clientGameLogic: ClientGameLogic) {
        this.clientGameLogic = clientGameLogic
        this.clientGameLogic.entityManager.registerRemoveListener((entity: Entity) => {
            this.interpolatable.delete(entity.entityId)
            this.renderSmoothing.delete(entity.entityId)
        })
        this.agentController = new AgentController(this)
    }

    boost = (on: boolean) => {
        this.send({ entityId: this.clientGameLogic.playerEntity.entityId, boost: on }, Protocol.type_boostAction)
    }

    chargedWeaponAction = (chargeAction: "begin" | "release" | "cancel", chargeId: string, target?: Coordinate) => {
        this.send({ message: { chargedWeaponAction: { chargeAction, chargeId, target } } }, Protocol.type_fireWeapon)
    }

    fireBullet = () => {
        const player = this.clientGameLogic.playerEntity
        if (!player) {
            return
        }

        if (player.weapon?.profileId) return
        if (!this.clientGameLogic.canFireBulletFrom(player)) {
            return
        }

        this.clientGameLogic.attack(player.entityId, () => {
            const bullets: Entity[] = this.clientGameLogic.createBulletFrom(player)
            if (!bullets?.length) {
                // could not fire bullet
                return
            }

            bullets.forEach(bullet => {
                bullet.entityId = randomId()
                this.clientGameLogic.entityManager.set(bullet)
                if (bullet.spriteId !== "none") {
                    this.clientGameLogic.clientRenderer.entity().create(bullet)
                }
            })

            const entities = bullets.map(bullet => {
                let message: Message = {
                    entityId: bullet.entityId,
                }
                return message
            })

            this.send(
                {
                    message: {
                        entities,
                        origin: { ...player.location },
                        roomId: player.roomId,
                        movement: { ...player.movement },
                        weapon: player.weapon,
                    },
                },
                Protocol.type_fireWeapon,
            )
        })
    }

    spawnNpc = () => {
        // if (this.clientGameLogic.playerEntity.name?.trim() !== "aron") {
        //     return
        // }

        this.send({ entityId: this.clientGameLogic.playerEntity.entityId }, "spawn")
    }

    dropAction = () => {
        this.send({ entityId: this.clientGameLogic.playerEntity.entityId }, Protocol.type_dropAction)
    }

    resetAction = () => {
        this.send({ entityId: this.clientGameLogic.playerEntity.entityId }, Protocol.type_resetAction)
    }

    activateInventoryItem = (item: string) => {
        this.send(
            {
                message: {
                    item,
                    originEntityId: this.clientGameLogic.playerEntity.entityId,
                },
            },
            Protocol.type_activateInventoryItem,
        )
    }

    activateAbility = (shortcutKey: string) => {
        this.send(
            {
                message: {
                    shortcutKey,
                    originEntityId: this.clientGameLogic.playerEntity.entityId,
                },
            },
            Protocol.type_activateAbility,
        )
    }

    dropActiveItem = () => {
        this.send({}, Protocol.type_requestDropActiveItem)
    }

    startChat = () => {
        this.clientGameLogic.clientRenderer.gui().startChat()
    }

    stopChat = () => {
        const message = this.clientGameLogic.clientRenderer.gui().stopChat()
        this.sendChatMessage(message)
    }

    // One line of chat, from whichever field the player typed it into -- the inline one the Enter
    // key opens or the composer a phone gets (gui/chat_composer.ts). Everything the game does with
    // a typed line beyond putting it on the wire lives here, so neither field has to know about it.
    sendChatMessage = (message: string) => {
        if (!message) {
            return
        }

        // Intercept `/agent ...` client-side: the AgentController handles it locally instead
        // of sending it to the server as a normal chat message.
        if (message.startsWith("/agent") && this.agentController) {
            this.agentController.handleCommand(message)
            return
        }

        this.send({ entityId: this.clientGameLogic.playerEntity.entityId, message }, Protocol.type_chatMessage)
    }

    // Keys that mean something to the chat input even though they type no character. Anything
    // else with a multi-character name is a modifier or a function key and never reaches the line.
    private static readonly CHAT_EDIT_KEYS = [
        "Backspace",
        "Delete",
        "Escape",
        "Home",
        "End",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
    ]

    captureKeystroke = (c: string, mods?: KeyModifiers) => {
        if (c.length > 1 && !Client.CHAT_EDIT_KEYS.includes(c)) {
            return
        }
        this.clientGameLogic.clientRenderer.gui().captureKeystroke(c, mods)
    }

    pasteIntoChat = (text: string) => {
        this.clientGameLogic.clientRenderer.gui().insertChatText(text)
    }

    updateName = (name: string) => {
        this.send(
            {
                message: {
                    name,
                    originEntityId: this.clientGameLogic.playerEntity.entityId,
                },
            },
            Protocol.type_updateName,
        )
    }

    updateEntityFromMessage = (message: Message, entity: Entity, now: number) => {
        entity.lastUpdateTs = now
        entity.entityState = message.entityState
        entity.roomId = message.roomId
        entity.speed = message.speed
        entity.maxSpeed = message.maxSpeed
        entity.name = message.name
        entity.faction = message.faction
        entity.color = message.color
        entity.hp = message.hp
        entity.hunger = message.hunger
        entity.maxHunger = message.maxHunger
        entity.maxHp = message.hpMax
        entity.joinedTeam = message.joinedTeam
        entity.score = message.score
        entity.level = message.level
        entity.scoreTilNextLevel = message.scoreTillNextLevel
        entity.weapon = message.weapon
        entity.paralyzeUntilTs = message.paralyzeUntilTs
        entity.appearance = message.appearance
        entity.clickable = message.clickable
        entity.spriteId = message.spriteId
        entity.noGlint = message.noGlint
        entity.expirationTs = message.expirationTs
        entity.quantity = message.quantity
        entity.lightMeta = message.lightMeta
        entity.spriteScale = message.spriteScale
        entity.leaderEntityId = message.leaderEntityId
        entity.rescuePose = message.rescuePose
        entity.rescueHelpHint = message.rescueHelpHint
        entity.chargedWeaponDefinition = message.chargedWeaponDefinition
        entity.weaponCharge = message.weaponCharge
        entity.areaProjectile = message.areaProjectile
        entity.visualEffects = message.visualEffects
        entity.animationPose = message.animationPose
        entity.attackWindup = message.attackWindup
        entity.bonecallerAttack = message.bonecallerAttack
        entity.bonecallerCombatRadius = message.bonecallerCombatRadius
        entity.bonecallerRitual = message.bonecallerRitual
        entity.bonecallerEmergenceRemainingMs = message.bonecallerEmergenceRemainingMs
        entity.downed = message.downed
        entity.holdRemainingMs = message.holdRemainingMs
        entity.holdDurationMs = message.holdDurationMs
        entity.contextMenuSubject = message.contextMenuSubject
        entity.campfireMenuLearned = message.campfireMenuLearned
        entity.recruitMenuLearned = message.recruitMenuLearned
        entity.flags = {}
        entity.flags[SIGIL_FLAG] = message.sigilIcon
        entity.dimensions = message.dimensions
        entity.debug = message.debug
        entity.afk = message.afk
        entity.active = message.active

        if (message.inventory) {
            entity.inventory = message.inventory
        }

        this.clientGameLogic.clientRenderer.entity().update(entity)
    }

    // Fold one freshly arrived frame's send timestamp into the server clock estimate.
    private observeServerClock = (serverTs?: number) => {
        if (typeof serverTs !== "number") {
            return
        }
        const observed = Date.now() - serverTs
        if (this.serverClockOffset === undefined || observed < this.serverClockOffset) {
            this.serverClockOffset = observed
            return
        }
        this.serverClockOffset += (observed - this.serverClockOffset) * Client.SERVER_CLOCK_CREEP
    }

    // The server's clock, as best we can read it from here. Position buffers are kept on THIS
    // timeline rather than on ours: server timestamps are evenly spaced and monotonic by
    // construction, so a re-estimate of the offset moves the playback cursor instead of
    // corrupting samples already in the buffer.
    private serverNow = () => Date.now() - (this.serverClockOffset ?? 0)

    /**
     * Put one freshly arrived frame on the queue, never letting it grow without bound.
     *
     * See MAX_QUEUED_WORLD_STATES for why a bound is needed at all. The oldest goes rather than the
     * newest: a stale frame has nothing to offer a renderer that is about to be handed a newer one,
     * and REPLAYING a long backlog would be worse than dropping it -- every frame runs
     * reconciliation and re-applies pending inputs, so a thousand of them arrive as a thousand
     * frames of prediction in a single tick.
     */
    private enqueueWorldState = (message: WorldState) => {
        this.incomingWorldStates.push(message)
        while (this.incomingWorldStates.length > Client.MAX_QUEUED_WORLD_STATES) {
            this.incomingWorldStates.shift()
            this.desynced = true
        }
    }

    /**
     * Throw away everything that went stale while the player was not looking, and ask the server
     * for a fresh picture of the world.
     *
     * Called on resume. The dropped frames cannot simply be caught up on: world state is a DELTA
     * stream, and the server marks each entity as sent per player (Server.playerEntitySentCache),
     * so a change that was broadcast while this client was ignoring it is one the server will never
     * mention again. Only a resend closes that gap, which is what type_resumeUpdates asks for.
     *
     * The local timing state goes too. The server-clock estimate creeps towards the truth over many
     * frames and is now minutes out of date; the interpolation buffers hold samples stamped on that
     * old estimate, and the render smoother holds positions from before the gap. Fed to
     * interpolateEntities as they are, they would draw every remote entity skating in from wherever
     * it stood when the player looked away.
     */
    private resyncAfterGap = () => {
        this.incomingWorldStates.length = 0
        this.desynced = false
        this.serverClockOffset = undefined
        this.renderSmoothing.clear()
        this.clientGameLogic.entityManager.iterate(null, (entity: Entity) => {
            entity.position_buffer = []
        })
    }

    /**
     * Send something the game can do without, on a socket that may already be gone.
     *
     * The lifecycle's messages are all sent from timers, so the socket can close between the check
     * that it was open and the send itself -- and `send` goes straight at it. An exception thrown
     * out of a timer callback would take the rest of that callback with it, including the state
     * that decides whether to reconnect.
     */
    private sendIfOpen = (payload: {}, payloadType: string) => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
            return
        }
        try {
            this.send(payload, payloadType)
        } catch (e) {
            logger.error({ tags: ["websocket", "error"] }, `Sending ${payloadType} threw`, String(e))
        }
    }

    /** The lifecycle's view of this client: what to do, and whether the socket is up. */
    private lifecycleDelegate = () => ({
        suspendUpdates: () => this.sendIfOpen({}, Protocol.type_suspendUpdates),
        resumeUpdates: () => {
            this.resyncAfterGap()
            this.sendIfOpen({}, Protocol.type_resumeUpdates)
        },
        reconnect: () => this.redialNow(),
        isConnected: () => this.ws?.readyState === WebSocket.OPEN,
        log: (message: string) => logger.info({ tags: ["websocket", "connection"] }, message),
    })

    /**
     * Drop the current socket and dial again with the same credentials.
     *
     * The old socket is closed with its handlers detached first. A socket being replaced because it
     * went silent is not necessarily dead, and its `close` landing after the replacement is up would
     * otherwise put the disconnected panel over a working connection.
     *
     * The client's own world is torn down with it. A new socket is a NEW SESSION -- the server
     * destroys the player on close, so there is nothing to resume into -- and it arrives with a
     * different entityId. Without this the previous avatar stays in the entity manager and drawn on
     * the stage, because the room-change teardown in updateFromWorldState deliberately spares
     * whatever `playerEntity` points at.
     */
    private redialNow = () => {
        if (!this.redial || this.injectedSocket) {
            return
        }
        const old = this.ws
        if (old) {
            old.onopen = null
            old.onmessage = null
            old.onerror = null
            old.onclose = null
            try {
                old.close()
            } catch (e) {
                logger.error({ tags: ["websocket", "connection"] }, "Closing the old socket threw", String(e))
            }
        }
        this.resyncAfterGap()
        this.clientGameLogic.prepareForReconnect()
        this.redial()
    }

    /**
     * The server has ended this session and told us why. Stay down.
     *
     * Everything that would dial again is taken away: the lifecycle (its poll sees a closed socket
     * and schedules a retry) and the credentials behind `redial`. Without that, being signed out by
     * a login somewhere else turns into a tug of war -- each tab's redial evicts the other about once
     * a second, forever, with both sockets live in between and both driving the same avatar.
     *
     * The panel goes up HERE rather than waiting for the close, so the player reads why they were
     * signed out instead of a "Connection lost / Try again" that is not true and whose retry would
     * take the account back off the window they moved to.
     */
    private handleSessionEnded = (reason?: string) => {
        this.sessionEndReason = reason || "ended"
        logger.info({ tags: ["websocket", "session"] }, `session ended by the server: ${this.sessionEndReason}`)
        this.lifecycle?.stop()
        this.lifecycle = undefined
        this.redial = undefined
        if (!this.injectedSocket) {
            showSessionEnded(this.sessionEndReason)
        }
    }

    // Handles messages from the server and updates client state
    processServerMessages = () => {
        // A frame was dropped somewhere, so parts of this world are as the player last saw them
        // rather than as they are. Deltas cannot fill that in; only a resend can. The usual cause
        // is the queue overflowing during the couple of seconds between the page being hidden and
        // the suspend taking effect, but a long main-thread stall does it too.
        if (this.desynced && this.resyncRequestLatch.expired()) {
            this.resyncAfterGap()
            this.sendIfOpen({}, Protocol.type_resumeUpdates)
        }

        const now = Date.now()
        while (true) {
            const worldState: WorldState = this.incomingWorldStates.shift()
            if (!worldState) {
                break
            }

            const updatedRoom = this.clientGameLogic.updateFromWorldState(worldState)
            if (updatedRoom) {
                // this.clientGameLogic.clientRenderer.gui().fadeOut(true)
            }

            const { messages } = worldState

            // World state is a list of entity states.
            for (let i = 0; i < messages.length; i++) {
                const message: Message = messages[i]

                if (message.interest === "leave") {
                    const leaving = this.clientGameLogic.getEntity(message.entityId)
                    if (leaving) {
                        // Fully evict out-of-interest entities. Keeping the renderable alive allows
                        // a stale sprite/interpolation state to resurface before the next snapshot.
                        this.clientGameLogic.clientRenderer.entity().destroy(leaving)
                        this.clientGameLogic.entityManager.remove(leaving.entityId)
                    }
                    this.interpolatable.delete(message.entityId)
                    this.renderSmoothing.delete(message.entityId)
                    this.clientGameLogic.invalidateEntitySortCache()
                    this.clientGameLogic.invalidateViewportCache()
                    continue
                }

                if (this.clientGameLogic.isDestroyed(message.entityId)) {
                    // its been destroyed, noop
                    continue
                }

                // If this is the first time we see this entity, create a local representation.
                if (!this.clientGameLogic.hasEntity(message.entityId)) {
                    this.clientGameLogic.createEntity(message)
                }

                const entity = this.clientGameLogic.getEntity(message.entityId)
                if (!entity) {
                    continue
                }

                this.updateEntityFromMessage(message, entity, now)

                // No `interest` field at all means the server is not interest-managing this entity
                // -- an immoveable npc, a player, a bullet -- so it is always live, the same reading
                // createEntity gives a first message that carries no interest. It is not only belt
                // and braces: an entity can STOP being interest-managed after the client has
                // already filed it as dormant. The server drops management the moment `immoveable`
                // turns true (see interestManaged in server.ts), so an npc spawned moveable out of
                // detail range -- the ember rescue survivor, revealed at the wreck while the player
                // is still at the fire -- and then pinned in place arrives dormant and is never
                // sent an "enter" edge again. Without this it stays dormant for the rest of the
                // session and render() hides it every frame, however close the player walks.
                const resumingInterest =
                    message.interest === "enter" ||
                    (message.interest === undefined && entity.interestState === "dormant")
                if (resumingInterest) {
                    entity.interestState = "active"
                    entity.position_buffer = []
                    this.renderSmoothing.delete(entity.entityId)
                    this.clientGameLogic.invalidateEntitySortCache()
                    this.clientGameLogic.invalidateViewportCache()
                } else if (message.interest === "update" && entity.interestState === "dormant") {
                    // A dormant entity must not become visible from an old cached position. It
                    // becomes active only when the server explicitly sends the interest edge.
                    continue
                }

                if (Mechanics.entity.body.isStationary(entity)) {
                    this.interpolatable.delete(entity.entityId)
                } else {
                    this.interpolatable.add(entity.entityId)
                }

                const player = this.clientGameLogic.playerEntity
                const isPlayer = message.entityId == player?.entityId

                // update other players locally
                if (isPlayer) {
                    if (!updatedRoom) {
                        const updatedLocation = { x: message.position[0], y: message.position[1] }
                        const positionUpdated =
                            updatedLocation.x !== this.clientGameLogic.playerEntity.location.x ||
                            updatedLocation.y !== this.clientGameLogic.playerEntity.location.y
                        this.clientGameLogic.updateFromPlayerMessage(message)

                        // At most one reconciliation per drawn frame (see interpolationTs). A
                        // standing player skips this and reconciles on every message: with no
                        // pending inputs there is nothing to replay, so it is nearly free.
                        if (player.pending_inputs.length > 0 && !this.interpolationTs.expired()) {
                            continue
                        }

                        // Received the authoritative position of this client's entity.
                        if (player.pending_inputs.length < 1 && positionUpdated) {
                            logger.debug(
                                { tags: ["player", "position"] },
                                "Player position updating from",
                                entity.location,
                                "to",
                                updatedLocation,
                            )
                            const renderedBefore = { x: entity.location.x, y: entity.location.y }
                            entity.location = updatedLocation
                            this.accumulatePlayerPositionError(renderedBefore, entity.location)
                            this.clientGameLogic.clientRenderer.updatePlayerLocation(entity)
                            // Invalidate caches when player position updates
                            this.clientGameLogic.invalidateEntitySortCache()
                            this.clientGameLogic.invalidateViewportCache()
                            logger.debug(
                                { tags: ["player", "initialization"] },
                                "Player initialized:",
                                this.clientGameLogic.isPlayerInitialized(),
                            )
                            continue
                        }
                        if (getCoordinateDistance(entity.location, updatedLocation) > TileSize / 2) {
                            this.primingLatch = null
                        }
                        // Capture where the player is currently rendered (last frame's predicted
                        // position) so we can measure the visual correction after reconciliation.
                        const renderedBefore = { x: entity.location.x, y: entity.location.y }
                        entity.location = updatedLocation
                        // Also invalidate caches in the fallback case
                        this.clientGameLogic.invalidateEntitySortCache()
                        this.clientGameLogic.invalidateViewportCache()

                        // Server Reconciliation. Re-apply all the inputs not yet processed by
                        // the server.
                        let j = 0

                        while (j < player.pending_inputs.length) {
                            const input = player.pending_inputs[j]
                            if (input.input_sequence_number <= message.last_processed_input) {
                                // Already processed. Its effect is already taken into account into the world update
                                // we just got, so we can drop it.
                                player.pending_inputs.splice(j, 1)
                            } else {
                                // Not processed by the server yet. Re-apply it.
                                const resultItem = ApplyInputResultPool.instance.checkout()
                                this.clientGameLogic.advanceEntity(resultItem.data, entity, input, true)
                                ApplyInputResultPool.instance.release(resultItem)
                                j++
                            }
                        }

                        // entity.location is now the corrected predicted position (truth). Fold the
                        // delta from where we were rendering into the smoothing offset instead of
                        // letting the camera jump. accumulatePlayerPositionError() snaps outright on
                        // large corrections (teleports/room changes).
                        this.accumulatePlayerPositionError(renderedBefore, entity.location)
                    } else {
                        setTimeout(() => {
                            entity.position_buffer = []
                            const updatedLocation = { x: message.position[0], y: message.position[1] }
                            this.clientGameLogic.updateFromPlayerMessage(message)
                            entity.location = updatedLocation
                            // force=true: this room-load call fires ~100ms after the
                            // camera's translation computer was reset, still inside its
                            // 150ms ready() gate. Without force it gets dropped and the
                            // camera stays off-player until the first movement.
                            this.clientGameLogic.clientRenderer.updatePlayerLocation(entity, true)
                        }, 100)
                    }
                } else {
                    this.clientGameLogic.updateFromNonPlayerMessage(message)

                    // Received the position of an entity other than this client's.
                    const updatedLocation = { x: message.position[0], y: message.position[1] }
                    // Stamp the sample with when the SERVER took it, not with `now`. `now` is the
                    // moment this frame got round to draining the queue, so it quantizes every
                    // sample to the frame period and collapses two frames drained together onto
                    // one timestamp. Interpolating over that renders a constant-speed walk as one
                    // that alternates between half speed and a jump.
                    const timestamp = worldState.serverTs ?? this.serverNow()

                    entity.location = updatedLocation
                    this.clientGameLogic.entityManager.set(entity)

                    if (!this.clientGameLogic.isTooDistantFromPlayerByLocation(entity.entityId, updatedLocation)) {
                        const buffer = entity.position_buffer
                        const previous = buffer[buffer.length - 1]
                        if (previous && previous[0] >= timestamp) {
                            // Two samples stamped the same server millisecond. A zero-length
                            // segment has no meaningful velocity and divides by zero in the lerp,
                            // so keep the newer position and drop the older.
                            buffer.pop()
                        }
                        buffer.push([
                            timestamp,
                            message.position[0],
                            message.position[1],
                            message.direction,
                            message.angle,
                            message.areaProjectile,
                        ])
                    } else {
                        // Interpolation is paused outside the viewport. Discard history and the
                        // render smoother there, or re-entry blends the new position from an old
                        // sample and can make an ordinary walking NPC jump several tiles.
                        entity.position_buffer.length = 0
                        this.renderSmoothing.delete(entity.entityId)
                    }

                    if (!message.complete) {
                        this.clientGameLogic.incompleteEntity(entity)
                    } else {
                        this.clientGameLogic.clearIncompleteEntities([entity.entityId])
                    }
                }

                entity.visible = message.visible
                // Beside `visible`, and for the same reason: a verdict about somebody ELSE that the
                // renderer has to act on. See Entity.invading -- it is what keeps the player ring off
                // a possessed attacker.
                entity.invading = message.invading
                // Also always-on: the survivor crouch must be right on a brief message too.
                entity.rescuePose = message.rescuePose
                // ...and the prompt to hold on her, for the same reason: it is sent on every
                // message, and a prompt that blinked out between detailed ones -- she waits at the
                // far end of the map, right at the edge of detail range -- would read as a bug.
                entity.rescueHelpHint = message.rescueHelpHint
                entity.chargedWeaponDefinition = message.chargedWeaponDefinition
                entity.weaponCharge = message.weaponCharge
                entity.areaProjectile = message.areaProjectile
                entity.visualEffects = message.visualEffects
                entity.animationPose = message.animationPose
                entity.attackWindup = message.attackWindup
                entity.bonecallerAttack = message.bonecallerAttack
                entity.bonecallerCombatRadius = message.bonecallerCombatRadius
                entity.bonecallerRitual = message.bonecallerRitual
                entity.noGlint = message.noGlint
                entity.bonecallerEmergenceRemainingMs = message.bonecallerEmergenceRemainingMs
                // Likewise the fallen pose. `downed` is one of the few verdicts the server puts on
                // EVERY message rather than only the detailed ones (see fillMessageFromEntity), and
                // a body left standing for the seconds between detailed messages is the whole tell.
                entity.downed = message.downed
                entity.contextMenuSubject = message.contextMenuSubject
                entity.campfireMenuLearned = message.campfireMenuLearned
                entity.recruitMenuLearned = message.recruitMenuLearned
            }
        }
    }

    private toSendState = (player: Entity) => {
        const { speed2: c, movement } = player
        if (!this.lastMovement) {
            this.lastMovement = { ...movement }
        }
        const dontSend = c.x === 0 && c.y === 0 && movement.angle === this.lastMovement.angle

        this.lastMovement.angle = movement.angle
        this.lastMovement.direction = movement.direction
        this.lastMovement.moving = movement.moving
        return !dontSend
    }

    // Process user inputs and send updated to the server
    processInputs = () => {
        const now_ts = Date.now()
        const last_ts = this.last_ts || now_ts
        const dt_sec = (now_ts - last_ts) / 1000.0
        this.last_ts = now_ts

        // dt_sec is the delta seconds since last update
        this.clientGameLogic.processEntities(dt_sec)
        const player = this.clientGameLogic.playerEntity

        // handle player movement

        if (player) {
            const { movement, speed } = player

            const thisState = this.toSendState(player)
            const changed = thisState !== this.lastState || this.lastState === undefined

            if (changed) {
                this.sendState = thisState
            }
            this.lastState = thisState
            if (!this.primingLatch) {
                this.primingLatch = new Latch(2000, Date.now())
                this.primingLatch.singleUse = true
            }

            if (this.sendState || (this.primingLatch && !this.primingLatch.expired())) {
                const speed2 = { x: player.speed2.x * speed, y: player.speed2.y * speed }

                let message: Message = {
                    entityType: "player",
                    press_time: dt_sec,
                    direction: movement.direction,
                    angle: movement.angle,
                    speed2,
                    input_sequence_number: player.input_sequence_number++,
                    entityId: player.entityId,
                    roomId: player.roomId,
                }

                const { x, y } = player.location
                const resultItem = ApplyInputResultPool.instance.checkout()
                this.clientGameLogic.advancePlayer(resultItem.data, message)
                const movedX = resultItem.data?.movedX
                const movedY = resultItem.data?.movedY
                ApplyInputResultPool.instance.release(resultItem)

                if (
                    (!movedX && movedY) ||
                    (movedX && !movedY && (x !== player.location.x || y !== player.location.y))
                ) {
                    if (movedX) {
                        const speed2 = { x: player.speed2.x * speed, y: 0 }
                        message.speed2 = speed2
                    }

                    if (movedY) {
                        const speed2 = { x: 0, y: player.speed2.y * speed }
                        message.speed2 = speed2
                    }
                }

                this.ws.send(
                    JSON.stringify({
                        recv_ts: Date.now(),
                        contentRevision,
                        contentProtocol: 1,
                        payload: message,
                    }),
                )

                // Save this input for later reconciliation.
                player.pending_inputs.push(message)
            }
        }
    }

    renderWorld = () => this.clientGameLogic.renderScene()

    // Ease the drawn position of one entity toward where interpolation says it is. Frame-rate
    // independent: the same time constant whatever the refresh rate.
    private smoothRenderPosition = (entityId: EntityId, targetX: number, targetY: number, now: number) => {
        const state = this.renderSmoothing.get(entityId)
        if (!state) {
            const created = { x: targetX, y: targetY, ts: now }
            this.renderSmoothing.set(entityId, created)
            return created
        }
        const elapsed = now - state.ts
        state.ts = now
        // Squared, because this runs for every visible entity every frame and Math.hypot is a
        // surprisingly expensive way to ask a yes/no question.
        const offByX = targetX - state.x
        const offByY = targetY - state.y
        const offBySq = offByX * offByX + offByY * offByY
        if (
            elapsed <= 0 ||
            offBySq > Client.RENDER_SMOOTHING_SNAP_PX_SQ ||
            offBySq < Client.RENDER_SMOOTHING_SETTLE_PX_SQ
        ) {
            state.x = targetX
            state.y = targetY
            return state
        }
        const alpha = 1 - Math.exp(-elapsed / Client.RENDER_SMOOTHING_TAU_MS)
        state.x += (targetX - state.x) * alpha
        state.y += (targetY - state.y) * alpha
        return state
    }

    interpolateEntities = () => {
        // Compute render timestamp, on the server's timeline (that is what position buffers are
        // stamped with -- see processServerMessages).
        const now = Date.now()
        const render_timestamp = this.serverNow() - Client.INTERPOLATION_DELAY_MS

        // Reset stats for this frame
        this.interpolationStats.totalInterpolatable = this.interpolatable.size
        this.interpolationStats.culledEntities = 0
        this.interpolationStats.actuallyInterpolated = 0
        this.interpolationStats.skippedNoBuffer = 0
        this.interpolationStats.skippedNotInterpolatable = 0
        this.interpolationStats.underran = 0

        this.interpolatable.forEach(entityId => {
            const entity = this.clientGameLogic.entityManager.get(entityId)

            if (!entity) {
                return
            }

            // No point in interpolating local things
            if (!this.clientGameLogic.shouldInterpolate(entity)) {
                this.interpolationStats.skippedNotInterpolatable++
                return
            }

            // Skip interpolation for culled entities
            if (this.clientGameLogic.isTooDistantFromPlayer(entity)) {
                this.interpolationStats.culledEntities++
                return
            }

            this.interpolationStats.actuallyInterpolated++

            // Find the two authoritative positions surrounding the rendering timestamp.
            const buffer = entity.position_buffer || []
            if (buffer.length < 1) {
                this.interpolationStats.skippedNoBuffer++
                return
            }

            // Drop samples already played past, keeping the one that brackets the render
            // timestamp from below.
            while (buffer.length >= 2 && buffer[1][0] <= render_timestamp) {
                buffer.shift()
            }

            // Always produce a position, even when the buffer cannot bracket the render
            // timestamp -- it runs dry whenever an update is late, and it has nothing old
            // enough for the first INTERPOLATION_DELAY_MS after an entity appears. Leaving
            // the entity alone on those frames used to hand the drawing over to the raw
            // latest position that processServerMessages had written straight onto it, so
            // the entity jumped a whole interpolation delay forward and then back again on
            // the next frame. Holding the nearest sample we do have and letting the smoother
            // ease into it is both continuous and closer to the truth.
            const from = buffer[0]
            const to = buffer.length >= 2 ? buffer[1] : undefined
            let targetX = from[1]
            let targetY = from[2]
            let facing = from
            if (to && from[0] <= render_timestamp) {
                const span = to[0] - from[0]
                const progress = span > 0 ? (render_timestamp - from[0]) / span : 1
                targetX = from[1] + (to[1] - from[1]) * progress
                targetY = from[2] + (to[2] - from[2]) * progress
                facing = to
            } else {
                this.interpolationStats.underran++
            }

            // Projectiles follow the authoritative straight path. Character easing makes a
            // stopped charge creep toward its landing spot; its phase must also follow the
            // buffered timeline so the warning does not begin while the orb is still travelling.
            let drawn: { x: number; y: number }
            if (entity.areaProjectile) {
                this.renderSmoothing.delete(entityId)
                const phase = from[5]
                if (phase) entity.areaProjectile = { ...phase,
                    remainingMs: Math.max(0, phase.remainingMs - Math.max(0, render_timestamp - from[0])) }
                drawn = { x: targetX, y: targetY }
            } else {
                drawn = this.smoothRenderPosition(entityId, targetX, targetY, now)
            }
            entity.location.x = drawn.x
            entity.location.y = drawn.y
            entity.movement.direction = facing[3]
            entity.movement.angle = facing[4]
        })

        // Log interpolation stats periodically
        if (this.interpolationLogLatch.expired()) {
            const stats = this.interpolationStats
            const cullRate =
                stats.totalInterpolatable > 0
                    ? ((stats.culledEntities / stats.totalInterpolatable) * 100).toFixed(1)
                    : "0"

            logger.info({ tags: ["interpolation", "stats"] }, "🔄 Interpolation Stats:")
            logger.info({ tags: ["interpolation", "stats"] }, `  Total interpolatable: ${stats.totalInterpolatable}`)
            logger.info(
                { tags: ["interpolation", "stats"] },
                `  Culled (too far): ${stats.culledEntities} (${cullRate}%)`,
            )
            logger.info({ tags: ["interpolation", "stats"] }, `  Actually interpolated: ${stats.actuallyInterpolated}`)
            logger.info({ tags: ["interpolation", "stats"] }, `  Skipped (no buffer): ${stats.skippedNoBuffer}`)
            logger.info(
                { tags: ["interpolation", "stats"] },
                `  Skipped (not interpolatable): ${stats.skippedNotInterpolatable}`,
            )
            logger.info({ tags: ["interpolation", "stats"] }, `  Buffer underran (held a sample): ${stats.underran}`)
            logger.info(
                { tags: ["interpolation", "stats"] },
                `  Performance: ${stats.actuallyInterpolated}/${stats.totalInterpolatable} entities processed`,
            )
        }
    }

    incompleteEntities = () => {
        if (this.incompleteEntityLatch.expired()) {
            const nearby = this.clientGameLogic.nearbyIncompleteEntities()
            if (nearby.length > 0) {
                this.requestEntityDetail(nearby)
            }
        }
    }

    // Accumulate the visual delta between where the player was being rendered and the
    // freshly reconciled (authoritative/predicted) position. Instead of letting the camera
    // jump to the correction, we carry the delta as an offset that decays over a few frames.
    // A correction larger than half a tile is treated as a teleport/room-change and snapped
    // outright (clearing any pending offset) rather than rubber-banded.
    private accumulatePlayerPositionError = (renderedBefore: Coordinate, corrected: Coordinate) => {
        if (getCoordinateDistance(renderedBefore, corrected) > TileSize / 2) {
            this.playerPositionError.x = 0
            this.playerPositionError.y = 0
            return
        }
        this.playerPositionError.x += renderedBefore.x - corrected.x
        this.playerPositionError.y += renderedBefore.y - corrected.y
    }

    /**
     * Where the local player is actually DRAWN this frame: the predicted/reconciled truth plus the
     * decaying correction offset. `playerEntity.location` on its own is NOT what the player sees --
     * a reconciliation moves it in a single step and this offset is precisely what hides that -- so
     * anything asking "did the screen jump" has to ask here. The render pass below computes the
     * same sum inline; this is the read-only way to sample it.
     */
    renderedPlayerLocation = (): Coordinate | undefined => {
        const player = this.clientGameLogic.playerEntity
        if (!player) {
            return undefined
        }
        return {
            x: player.location.x + this.playerPositionError.x,
            y: player.location.y + this.playerPositionError.y,
        }
    }

    // Decay the smoothing offset toward zero by one frame. Called at the TOP of the frame
    // (before reconciliation folds in any fresh error) so that on the frame a correction
    // arrives the offset renders at full strength -- exactly cancelling the positional jump --
    // and only begins easing on subsequent frames. Not private: the headless harness drives the
    // frame loop a piece at a time and has to run this one too, or its clients accumulate a
    // correction offset that never eases and stop resembling a real client.
    decayPlayerPositionError = () => {
        this.playerPositionError.x *= Client.POSITION_ERROR_DECAY
        this.playerPositionError.y *= Client.POSITION_ERROR_DECAY
        if (Math.abs(this.playerPositionError.x) < Client.POSITION_ERROR_SNAP_EPSILON) {
            this.playerPositionError.x = 0
        }
        if (Math.abs(this.playerPositionError.y) < Client.POSITION_ERROR_SNAP_EPSILON) {
            this.playerPositionError.y = 0
        }
    }

    // Game update loop
    update = () => {
        // Decay first, then let processServerMessages() fold in this frame's correction.
        this.decayPlayerPositionError()

        this.processServerMessages()

        if (!this.clientGameLogic.playerEntity) {
            return
        }

        const roomId = this.clientGameLogic.playerEntity.roomId
        const changedRoom = roomId !== this.spriteLabRoomId
        if (changedRoom) {
            this.spriteLabRoomId = roomId
            this.spriteLabVisible = false
            this.updateSpriteLab(null)
        }
        if (changedRoom || ((this.spriteLabVisible || roomId?.startsWith("spritelab-")) && Date.now() >= this.spriteLabPollAt)) {
            this.spriteLabPollAt = Date.now() + 2000
            this.send({ action: "status" }, Protocol.type_spriteLabRequest)
        }

        // Re-apply the agent's held movement intent after reconciliation and before
        // processInputs reads speed2 (reconciliation overwrites it every frame). No-op
        // unless the agent is actively driving.
        this.agentController?.onFrame()

        this.processInputs()

        this.interpolateEntities()

        this.incompleteEntities()

        // Render the player (and camera, which follows it) at the smoothed position:
        // truth + the decaying error offset. entity.location stays the authoritative/predicted
        // truth so the next frame's prediction and reconciliation are unaffected; we offset
        // only for the render/camera pass and restore truth immediately after.
        const player = this.clientGameLogic.playerEntity
        const offset = this.playerPositionError
        const hasOffset = offset.x !== 0 || offset.y !== 0
        if (hasOffset) {
            const truthX = player.location.x
            const truthY = player.location.y
            player.location.x = truthX + offset.x
            player.location.y = truthY + offset.y
            this.clientGameLogic.clientRenderer.updatePlayerLocation(player)
            this.renderWorld()
            player.location.x = truthX
            player.location.y = truthY
        } else {
            this.renderWorld()
        }

        this.clientGameLogic.cleanup()

        // Last, so it judges the scene the frame just drew: a room held black since it landed is
        // opened on the frame its map and its bodies are all actually there. See RoomReveal.
        this.clientGameLogic.tickRoomReveal()
    }

    send = (payload: {}, payloadType: string) => {
        this.ws.send(
            JSON.stringify({
                recv_ts: Date.now(),
                contentRevision,
                contentProtocol: 1,
                payloadType,
                payload,
            }),
        )
    }

    register = (username: string, password: string, email?: string) => {
        this.send(
            {
                username,
                password,
                email,
            },
            Protocol.type_upgradeGuest,
        )
    }

    login = (username: string, password: string) => {
        this.send(
            {
                username,
                password,
            },
            Protocol.type_login,
        )
    }

    logActivity = (activity: string) => {
        this.send(
            {
                activity,
            },
            Protocol.type_logActivity,
        )
    }

    submitFeedback = (feedback: SubmittedFeedback) => {
        this.send(
            {
                feedback,
            },
            Protocol.type_submitFeedback,
        )
    }

    handleGuest = (userAgent: string) => {
        this.clientGameLogic.clientRenderer
            .gui()
            .getIntro()
            .init((playerName: string, password: string) => {
                if (!password) {
                    this.connectImpl("", undefined, true, () => {
                        this.send({ playerName, userAgent }, "guest")
                    })
                } else {
                    this.connectImpl("", undefined, true, () => {
                        this.login(playerName, password)
                    })
                }
            })
    }

    invokeStatUpgradeUI = () => {
        this.send({}, Protocol.type_showStatUpgradeUI)
    }

    entityClicked = (entity: Entity) => {
        this.send(
            {
                entityId: entity.entityId,
            },
            Protocol.type_entityClicked,
        )
    }

    connect(playerName: string, accessToken?: string, guest?: boolean) {
        if (guest) {
            const userAgent = navigator?.userAgent
            this.handleGuest(userAgent)
        } else {
            // No login screen on this path, so there is no Play button to expand from -- take the
            // next tap instead. handleGuest's intro hangs it off Play/Log in itself.
            enterFullscreenOnFirstGesture()
            this.connectImpl(playerName, accessToken, false)
        }
    }

    /**
     * Headless/testing entry point: drive the REAL client protocol over an injected,
     * WebSocket-shaped transport (e.g. an in-memory loopback) instead of a browser socket.
     * Reuses the exact onmessage handshake/netcode; only socket construction and DOM
     * redirects are bypassed. Not used in the browser.
     */
    connectHeadless(
        ws: any,
        auth:
            | { mode: "login"; username: string; password: string }
            | { mode: "name"; playerName: string }
            | { mode: "guest"; playerName?: string },
    ) {
        this.injectedSocket = ws
        if (auth.mode === "login") {
            this.loginCredentials = { username: auth.username, password: auth.password }
            this.connectImpl("", undefined, false)
        } else if (auth.mode === "guest") {
            // Mirror handleGuest: the browser's guest flow sends the "guest" payload after
            // the negotiated session greeting (the intro screen supplies the name). Without
            // that callback the only payload was "initializeGuest", which the server does not act
            // on, so a headless guest never actually joined.
            const userAgent = navigator?.userAgent
            const playerName = auth.playerName
            this.connectImpl("", undefined, true, () => {
                this.send({ playerName, userAgent }, "guest")
            })
        } else {
            this.connectImpl(auth.playerName, undefined, false)
        }
    }

    connectImpl(playerName: string, accessToken?: string, guest?: boolean, onConnect?: () => void) {
        // What a redial means: the same door, the same credentials. Recorded here rather than
        // threaded through every caller, so the lifecycle and the disconnected panel can both ask
        // for one without knowing how this client got connected in the first place.
        this.redial = () => this.connectImpl(playerName, accessToken, guest, onConnect)

        const hostname = document.location.hostname
        const config = resolveConfig()
        // Both the dev-vs-production split and the ws:// vs wss:// choice now live in
        // runtime_config, because the shell is served from "localhost" too and a hostname test
        // here cannot tell it apart from a laptop.
        const isLocal = !config.isNative && (hostname === "localhost" || hostname.startsWith("10.0"))

        const ws = this.injectedSocket ? this.injectedSocket : new WebSocket(config.apiHost)

        if (isLocal && !this.injectedSocket) {
            installNetworkConditioner(ws)
        }

        const redirectToPortal = () => {
            if (accessToken) {
                if (isLocal) {
                    window.location.href = "http://localhost:3000/account/login"
                } else {
                    window.location.href = "https://projectsamsara.com/?guest=true"
                }
            } else {
                if (!isLocal) {
                    alert("The server might be down, please try again in a few minutes.")
                }
                window.location.href = "/"
            }
        }
        // Where a lost socket leaves the player. A desktop browser can send them back to the
        // portal, which is a real place with a real login. The shell cannot: its origin IS the app
        // bundle, so "/" reloads the app and an alert() freezes the WebView behind a modal that
        // dismisses into nothing. And on a phone generally, answering "the wifi blinked" by
        // discarding the session and reloading is hostile. Both get a panel with a retry instead.
        const offerRetryRatherThanRedirect = config.isNative || isMobile()

        const connectionLost = () => {
            // Not lost: ENDED, by the server, for a reason the player has already been given. Its
            // panel is up and there is nothing to retry.
            if (this.sessionEndReason) {
                return
            }
            // A connection we HAD and lost is a different situation from one we never made, and
            // navigating away is only ever right for the second. The server destroys the player --
            // and with them their ember camp -- the moment a socket closes, so a reload spends a
            // run that a reconnect in place would have let the player start again from the panel.
            // A first connection that never opened is still the portal's business: an expired
            // access token looks exactly like this, and the portal is where a new one comes from.
            if (this.everConnected) {
                showDisconnected({
                    everConnected: true,
                    onRetry: () => {
                        // The lifecycle owns dialling, so that a press and a pending backoff
                        // cannot both fire. It only exists in the browser.
                        if (this.lifecycle) {
                            this.lifecycle.retryNow()
                        } else {
                            this.redialNow()
                        }
                    },
                })
                return
            }
            if (offerRetryRatherThanRedirect) {
                showDisconnected({
                    everConnected: false,
                    // Dial the same way, with the same credentials. The panel stays up until a
                    // socket actually opens, so a failed retry needs no handling of its own.
                    onRetry: () => this.connectImpl(playerName, accessToken, guest, onConnect),
                })
                return
            }
            redirectToPortal()
        }

        // Only the socket this client is currently holding gets to report anything. A socket
        // replaced because it went silent (see redialNow) is not necessarily dead, and its events
        // arriving late would otherwise tear down the connection that replaced it.
        const isCurrent = () => this.ws === ws

        this.contentParticipant?.dispose()
        this.ws = ws
        ws.onopen = () => {
            if (!isCurrent()) {
                return
            }
            logger.info({ tags: ["websocket", "connection"] }, "Connected")
            this.everConnected = true
            // Covers the first connection as well as a retry: if the panel is not up, this is a
            // no-op.
            hideDisconnected()
            this.clientGameLogic.audio.setBonecallerAudioConnected(true)
            this.lifecycle?.noteConnected()
        }
        ws.onerror = e => logger.error({ tags: ["websocket", "error"] }, JSON.stringify(e))
        ws.onclose = () => {
            if (!isCurrent()) {
                return
            }
            this.updateSpriteLab(null)
            this.spriteLabRoomId = undefined
            this.spriteLabVisible = false
            this.contentParticipant?.dispose()
            this.clientGameLogic.audio.setBonecallerAudioConnected(false)
            // Headless/test sockets have no browser portal to redirect to, and no document to put
            // a panel in.
            if (!this.injectedSocket) {
                this.lifecycle?.noteConnectionLost()
                connectionLost()
            }
        }
        // One lifecycle for the whole session, however many sockets it gets through. Not in
        // headless mode: there is no page to hide and no socket to replace.
        if (!this.injectedSocket && !this.lifecycle) {
            this.lifecycle = new ConnectionLifecycle(this.lifecycleDelegate())
            this.lifecycle.start()
        }

        const userAgent = navigator?.userAgent
        let negotiating = false
        const authenticate = () => {
            logger.debug({ tags: ["websocket", "session"] }, "Received start session")
            if (this.loginCredentials) {
                // Headless/test login: authenticate an existing account by username/password.
                this.login(this.loginCredentials.username, this.loginCredentials.password)
            } else if (guest) {
                this.send({ userAgent }, "initializeGuest")
            } else {
                if (accessToken) {
                    logger.debug({ tags: ["websocket", "auth"] }, "Sent access token")
                    this.send({ accessToken, userAgent }, "accessToken")
                } else {
                    logger.debug({ tags: ["websocket", "auth"] }, "Sent playername", playerName)
                    this.send({ playerName, userAgent }, "name")
                }
            }

            onConnect?.()
        }
        const participant = new ContentParticipant(contentRuntime, (snapshot, signal, inline) => {
            const stage = this.clientGameLogic.clientRenderer.stageContent
            if (!stage) return Promise.reject(new Error("Renderer does not support live content"))
            return stage(snapshot, signal, inline)
        }, reply => {
            if (!isCurrent()) return
            if (reply.kind === "failed") logger.warn({ tags: ["content"] }, `Could not prepare published content ${reply.revision}: ${reply.reason}`)
            if (reply.kind === "activated") {
                this.resyncAfterGap()
                this.playerPositionError = { x: 0, y: 0 }
                if (this.clientGameLogic.playerEntity) this.clientGameLogic.playerEntity.pending_inputs = []
                this.clientGameLogic.clientRenderer.refreshContent?.()
            }
            this.send(reply, Protocol.type_contentReply)
            if (reply.kind === "failed") {
                // A client that cannot use the current content must reload, not keep playing
                // against an older release or repeatedly retry the same incompatible bundle.
                this.handleSessionEnded("contentRevisionMismatch")
                ws.close()
                return
            }
            if (reply.kind === "activated" && negotiating) { negotiating = false; authenticate() }
        })
        this.contentParticipant = participant
        ws.onmessage = message => {
            if (!isCurrent()) {
                return
            }
            // Anything at all arriving is the only proof this socket is alive -- a resumed WebView
            // holds one that reports OPEN forever and delivers nothing.
            this.lifecycle?.noteTraffic()
            const parsedMessage: MessageEnvelope = JSON.parse(message.data)
            const { payloadType, payload } = parsedMessage
            if (payloadType === Protocol.type_contentCommand) {
                if (!payload || typeof payload.id !== "string" || typeof payload.revision !== "string") return
                if (payload.kind === "prepare") void participant.prepare(payload, payload.manifest, payload.catalog, payload.files)
                else if (payload.kind === "activate") participant.activate(payload)
                else if (payload.kind === "abort") participant.abort(payload)
                return
            }
            if (payloadType === Protocol.type_worldState && parsedMessage.contentRevision && parsedMessage.contentRevision !== contentRevision) return
            if (payloadType === Protocol.type_startSession) {
                try {
                    requireContentRevision(payload?.contentRevision)
                } catch (error) {
                    if (payload?.contentProtocol === 1 && this.clientGameLogic.clientRenderer.stageContent) {
                        negotiating = true
                        this.send({}, Protocol.type_contentRequest)
                        return
                    }
                    logger.error({ tags: ["websocket", "content"] }, String(error))
                    this.handleSessionEnded("contentRevisionMismatch")
                    ws.close()
                    return
                }
                authenticate()

                return
            }

            // The server is ending this session on purpose, and said so before closing. The close
            // that follows must NOT be answered with a redial: it is not a dropped connection, and
            // dialling back in would take the account off whoever claimed it.
            if (payloadType === Protocol.type_sessionEnded) {
                participant.dispose()
                this.handleSessionEnded(payload?.reason)
                return
            }

            if (payloadType === Protocol.type_setEntityId) {
                logger.debug({ tags: ["websocket", "entity"] }, "Received entityId; guest:", guest === true)
                const { entityId } = payload
                this.clientGameLogic.initializePlayer(entityId, this, guest === true)
                if (guest) {
                    this.clientGameLogic.clientRenderer.gui().getIntro().loginSuccess()
                }
                return
            }

            if (payloadType === Protocol.type_updatePlayerEntityId) {
                logger.debug({ tags: ["websocket", "entity"] }, "Received update entityId")
                const { entityId } = payload
                this.clientGameLogic.updatePlayerEntityId(entityId)
                return
            }

            if (payloadType === Protocol.type_destroyEntityId) {
                const { entityId, roomId } = payload
                if (this.clientGameLogic.playerEntity?.roomId === roomId) {
                    this.clientGameLogic.scheduleForDestruction(entityId)
                }
            }

            if (payloadType === Protocol.type_ping) {
                const { entityId, creationTs } = payload
                this.send({ creationTs, entityId }, "pong")
            }

            if (payloadType === Protocol.type_sendPleaseWait) {
                this.clientGameLogic.pleaseWait()
            }

            if (payloadType === Protocol.type_fragmentAnimation) {
                const { spriteId, location } = payload
                this.clientGameLogic.clientRenderer.entity().fragment(spriteId, location)
            }

            if (payloadType === Protocol.type_struckEntityId) {
                const { entityId, strikerEntityId } = payload
                this.clientGameLogic.doStruck(entityId, strikerEntityId)
            }

            if (payloadType === Protocol.type_entityTileEffect) {
                const { effect } = payload
                this.clientGameLogic.tileEffect(effect)
            }

            if (payloadType === Protocol.type_detonate) {
                const { entityId } = payload
                this.clientGameLogic.detonate(entityId)
            }

            if (payloadType === Protocol.type_attack) {
                const { entityId } = payload
                // Add callback for weapon attack effects
                this.clientGameLogic.attack(entityId, () => {
                    // Callback for attack completion - can be used for weapon effects
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Server-triggered attack completed for entity ${entityId}`,
                    )
                })
            }

            if (payloadType === Protocol.type_playAttackAnimation) {
                // Cosmetic swing (e.g. another player placing a build tile). Play ONLY the sprite
                // animation -- not clientGameLogic.attack, which would also fire startAttack + audio.
                const { entityId } = payload
                const entity = this.clientGameLogic.entityManager.get(entityId)
                if (entity) {
                    this.clientGameLogic.clientRenderer.entity().attack(entity)
                }
            }

            if (payloadType === Protocol.type_updateActivation) {
                const { entityId, active } = payload
                this.clientGameLogic.updateActivation(entityId, active)
            }

            if (payloadType === Protocol.type_orientation) {
                const { orientation } = payload
                this.clientGameLogic.orientation(orientation)
            }

            if (payloadType === Protocol.type_killed) {
                const { entityId } = payload
                this.clientGameLogic.killed(entityId)
            }

            if (payloadType === Protocol.type_respawned) {
                const { entityId } = payload
                this.clientGameLogic.respawned(entityId)
            }

            if (payloadType === Protocol.type_sendTeleported) {
                const { entityId } = payload
                this.clientGameLogic.teleported(entityId)
            }

            if (payloadType === Protocol.type_entityEmp) {
                const { entityId, range } = payload
                this.clientGameLogic.emp(entityId, range)
            }

            if (payloadType === Protocol.type_entitySparkle) {
                const { entityId } = payload
                this.clientGameLogic.sparkle(entityId)
            }

            if (payloadType === Protocol.type_fadeInOut) {
                const { direction, transition } = payload
                // this is a hack; we do this so that the
                // map renderer is in charge of the "in" fade in after it does all its work
                if (direction === "out") {
                    // `transition`, when there is one, turns this into a scene change rather than a
                    // snap to black: see Protocol.TransitFade. The "in" stays the map renderer's.
                    this.clientGameLogic.fadeInOut(direction, undefined, transition)
                }
            }

            if (payloadType === Protocol.type_worldState) {
                const message: WorldState = payload
                // Carry the server's own send timestamp through to interpolation, and update the
                // clock estimate from it HERE rather than in processServerMessages: the offset is
                // only meaningful against the moment the frame landed, not the moment the next
                // frame got round to draining the queue.
                message.serverTs = parsedMessage.recv_ts
                this.observeServerClock(parsedMessage.recv_ts)
                this.enqueueWorldState(message)
            }

            if (payloadType === Protocol.type_entityDisconnected) {
                const { entityId } = payload
                logger.info({ tags: ["websocket", "disconnect"] }, "disconnect", entityId)
                this.clientGameLogic.scheduleForDestruction(entityId)
            }

            if (payloadType === Protocol.type_screenMessage) {
                const { message } = payload
                if (message) {
                    this.clientGameLogic.clientRenderer.gui().addScreenMessage(message)
                }
            }

            if (payloadType === Protocol.type_chatMessage) {
                const { message, originEntityName, originEntityId, hold, holdId, autoAdvance } = payload
                this.clientGameLogic.handleChat(message, originEntityId, originEntityName, hold, holdId, autoAdvance)
                this.agentController?.noteChat(originEntityName || originEntityId || "?", message)
            }

            if (payloadType === Protocol.type_roomAmbience) {
                const { ambience } = payload
                if (ambience) {
                    this.clientGameLogic.setRoomAmbience(ambience)
                }
            }

            if (payloadType === Protocol.type_factionTracking) {
                const { config } = payload
                if (config) {
                    this.clientGameLogic.setFactionTracking(config)
                }
            }

            if (payloadType === Protocol.type_redrawClient) {
                // Incremental repaint of just the delta-changed cells (falls back to a full rerender
                // internally if a tilemap-layer cell changed). Replaces the old full map().rerender().
                this.clientGameLogic.clientRenderer.map().onRedraw()
            }

            if (payloadType === Protocol.type_hideRoomName) {
                const { hidden, roomName } = payload
                this.clientGameLogic.hideRoomName(hidden)
                if (!hidden && roomName) {
                    this.clientGameLogic.updateLocation(roomName)
                }
            }
            if (payloadType === Protocol.type_sendUpdateLocation) {
                const { location, roomId } = payload
                this.clientGameLogic.updateLocation(location, roomId)
            }
            if (payloadType === Protocol.type_createLocalEntity) {
                const { entity } = payload
                this.clientGameLogic.createLocalEntity(entity)
            }
            if (payloadType === Protocol.type_destroyLocalEntity) {
                const { flags } = payload
                this.clientGameLogic.destroyLocalEntity(flags)
            }
            if (payloadType === Protocol.type_soundEffect) {
                const { effect, loop, target, source } = payload
                this.clientGameLogic.playSoundEffect(effect, loop, target, source)
            }
            if (payloadType === Protocol.type_sendAnimateLocalEntityCollection) {
                const { entityId, target, position } = payload
                this.clientGameLogic.animateLocalEntityCollection(entityId, target, position)
            }
            if (payloadType === Protocol.type_animateAbility) {
                const { abilityType, duration } = payload
                this.clientGameLogic.animateAbility(abilityType, duration)
            }
            if (payloadType === Protocol.type_upgradeGuest) {
                this.clientGameLogic.upgradedAccount()
            }
            if (payloadType === Protocol.type_dayPhase) {
                const { dayPhase, dayPct } = payload
                this.clientGameLogic.updateDayPhase(dayPhase)
                this.clientGameLogic.clientRenderer.gui().updateDayPct(dayPct)
            }
            if (payloadType === Protocol.type_requestEntityDetail) {
                const { messages } = payload
                if (messages) {
                    const now = Date.now()
                    messages.forEach((message: Message) => {
                        const entity = this.clientGameLogic.getEntity(message.entityId)
                        if (entity) {
                            this.updateEntityFromMessage(message, entity, now)
                            this.clientGameLogic.clearIncompleteEntities([entity.entityId])
                        }
                    })
                }
            }
            if (payloadType === Protocol.type_login) {
                const { entityId } = payload
                if (entityId) {
                    this.clientGameLogic.clientRenderer.gui().getIntro().loginSuccess()
                    this.clientGameLogic.initializePlayer(entityId, this, false)
                    this.clientGameLogic.clientRenderer.gui().addScreenMessage({
                        target: "important",
                        text: "Welcome back!",
                        delay: 1000,
                    })
                } else {
                    // The intro shows the failure under the password field and keeps what was
                    // typed; only fall back to the screen-wide banner if it could not.
                    const shownInline = this.clientGameLogic.clientRenderer.gui().getIntro().loginFailed()
                    if (!shownInline) {
                        this.clientGameLogic.clientRenderer.gui().addScreenMessage({
                            target: "important",
                            text: "Invalid player name or password.",
                        })
                    }
                }
            }
            if (payloadType === Protocol.type_mapDelta) {
                const deltas: MapDelta[] = payload.delta
                this.clientGameLogic.clientRenderer.map().queueMapDelta(deltas)
            }
            if (payloadType === Protocol.type_updateSquadState) {
                const { squadState } = payload
                this.clientGameLogic.clientRenderer.gui().updateSquadState(squadState)
            }
            if (payloadType === Protocol.type_updateEmberAnxiety) {
                this.clientGameLogic.clientRenderer.gui().updateEmberAnxiety(payload.anxiety || null, payload.roomId)
            }
            if (payloadType === Protocol.type_updateEmberRoster) {
                const { roster } = payload
                this.clientGameLogic.clientRenderer.gui().updateEmberRoster(roster || [])
                this.clientGameLogic.setEmberRoster(roster || [])
            }
            if (payloadType === Protocol.type_updateEmberMinimap) {
                const { minimap } = payload
                this.clientGameLogic.clientRenderer.gui().updateEmberMinimap(minimap || null)
            }
            if (payloadType === Protocol.type_spriteLabState) {
                const state = payload.state
                if (!state || state.roomId === this.clientGameLogic.playerEntity?.roomId) {
                    this.spriteLabVisible = !!state
                    this.updateSpriteLab(state, payload.message)
                }
            }
            if (payloadType === Protocol.type_updateEmberPlayers) {
                const { players } = payload
                this.clientGameLogic.clientRenderer.gui().updateEmberPlayers(players || [])
            }
            if (payloadType === Protocol.type_updateEmberInvite) {
                const { invite } = payload
                this.clientGameLogic.clientRenderer.gui().updateEmberInvite(invite || null)
            }
            if (payloadType === Protocol.type_updateEntityInteractions) {
                const { entityId, interactions } = payload
                this.clientGameLogic.clientRenderer.entity().updateEntityInteractions(entityId, interactions)
            }
            if (payloadType === Protocol.type_openCommandWheel) {
                const { entityId, role, commands } = payload
                this.clientGameLogic.clientRenderer.gui().showCommandWheel({ targetEntityId: entityId, role, commands })
            }
            if (payloadType === Protocol.type_openRolePicker) {
                // `currentRole` is present only for a "Change Role" order on an already-hired member,
                // and narrows the picker to the job it is NOT doing. `weapon` is what it would fight
                // with, for the Defender card -- sent by the server because this client may not hold
                // the member's pack at all (only NPCs within detail range carry one here).
                const { entityId, currentRole, weapon } = payload
                this.clientGameLogic.clientRenderer.gui().showRolePicker(entityId, currentRole, { weapon })
            }
            if (payloadType === Protocol.type_openNamePrompt) {
                const { entityId, name } = payload
                this.clientGameLogic.clientRenderer.gui().showNamePrompt(entityId, name)
            }
            if (payloadType === Protocol.type_showRunSummary) {
                const { summary } = payload
                this.clientGameLogic.clientRenderer.gui().showRunSummary(summary)
            }
            // Which camps a fallen player could rise in. An interval FEED (~2s) rather than a reply
            // to a press, so the scorecard's "Resurrect as Undead!" button lights up by itself when
            // one opens, and an open picker gains and loses cards as it is read. An empty list is a
            // real answer ("no camp is open to the dead"); null forgets the offer.
            if (payloadType === Protocol.type_updateInvasionOffer) {
                const { candidates } = payload
                this.clientGameLogic.clientRenderer.gui().updateInvasionOffer(candidates || null)
            }
            if (payloadType === Protocol.type_showTutorialPrompt) {
                const { prompt } = payload
                this.clientGameLogic.clientRenderer.gui().showTutorialPrompt(prompt || null)
            }
            if (payloadType === Protocol.type_updateEntityPointerIcon) {
                const { entityId, show, angle } = payload
                const entity = this.clientGameLogic.entityManager.get(entityId)
                if (entity) {
                    this.clientGameLogic.clientRenderer.entity().updateEntityPointerIcon(entity, show, angle)
                }
            }
            if (payloadType === Protocol.type_buildCharacter) {
                const { entityId } = payload
                this.clientGameLogic.clientRenderer.gui().buildCharacter(entityId)
            }
            if (payloadType === Protocol.type_itemPickedUp) {
                const { entityId, itemTypeMeta, quantity } = payload
                const entity = this.clientGameLogic.entityManager.get(entityId)
                if (entity) {
                    this.clientGameLogic.itemPickup(entity, itemTypeMeta, quantity)
                }
            }
            if (payloadType === Protocol.type_showTutorial) {
                // A null tutorialType is the server taking the ember clues back down (see
                // client_proxy.showTutorial); timeoutMs is how long a raised clue holds.
                const { tutorialType, timeoutMs } = payload

                this.clientGameLogic.clientRenderer.gui().showTutorial(tutorialType || null, timeoutMs)
            }
            if (payloadType === Protocol.type_builderAction) {
                const { active } = payload
                this.clientGameLogic.playerController.builderActivation(active)
            }
            if (payloadType === Protocol.type_sendWorldMap) {
                const { map } = payload
                this.clientGameLogic.clientRenderer.gui().showWorldMap(map)
            }

            if (payloadType === Protocol.type_removeLocationIcon) {
                this.clientGameLogic.clientRenderer.map().removeLocationIcon(payload.iconId)
            }

            if (payloadType === Protocol.type_addLocationIcon) {
                this.clientGameLogic.clientRenderer.entity().updateLocationIcons(payload.icon)
            }

            if (payloadType === Protocol.type_updatePlayerBiomeColorMap) {
                const points: BiomeMapPoint[] = payload.meta?.points || []
                const icons: LocationIcon[] = payload.meta?.icons || []
                const clear: boolean = payload?.meta?.clear === true

                if (clear) {
                    const biomeColorMap = this.clientGameLogic.tilemapMetaProvider?.biomeColorMap
                    if (biomeColorMap) {
                        for (let x = 0; x < biomeColorMap.length; x++) {
                            for (let y = 0; y < biomeColorMap[0].length; y++) {
                                biomeColorMap[x][y] = 0
                            }
                        }
                        this.clientGameLogic.clientRenderer
                            .map()
                            .updateMinimap(this.clientGameLogic.tilemapMetaProvider)
                    }
                }

                points.forEach(point => {
                    if (
                        this.clientGameLogic.tilemapMetaProvider &&
                        this.clientGameLogic.tilemapMetaProvider.minimapConfig?.biomeBased &&
                        this.clientGameLogic.tilemapMetaProvider.biomeColorMap
                    ) {
                        this.clientGameLogic.tilemapMetaProvider.biomeColorMap[point.biomeCoordinate.x][
                            point.biomeCoordinate.y
                        ] = point.color
                    }
                    this.clientGameLogic.clientRenderer.map().updateBiomeColorMap(point.biomeCoordinate, point.color)
                })

                icons.forEach(icon => {
                    if (this.clientGameLogic.tilemapMetaProvider) {
                        if (
                            !this.clientGameLogic.tilemapMetaProvider.locationIcons.find(
                                next => next.x === icon.x && next.y === icon.y,
                            )
                        ) {
                            this.clientGameLogic.tilemapMetaProvider.locationIcons.push(icon)
                        }
                    }
                    this.clientGameLogic.clientRenderer.entity().updateLocationIcons(icon)
                })
            }
            if (payloadType === Protocol.type_pulseInventoryItem) {
                const { itemId } = payload
                this.clientGameLogic.clientRenderer.gui().pulseInventoryItem(itemId)
            }
            if (payloadType === Protocol.type_entityFeedback) {
                const { entityId, image, label, params } = payload
                const entity = this.clientGameLogic.entityManager.get(entityId)
                if (entity) {
                    this.clientGameLogic.clientRenderer.entity().feedback(entity, image, label, params)
                }
            }
            if (payloadType === Protocol.type_sendVisibility) {
                const { visibility } = payload
                const currentVisibility = this.clientGameLogic.clientRenderer.map().getVisibility()
                this.clientGameLogic.clientRenderer.map().update(null, {
                    ...(currentVisibility || {}),
                    ...visibility,
                })
            }
            if (payloadType === Protocol.type_locationIconEffect) {
                const { locationId, expires } = payload

                this.clientGameLogic.clientRenderer.entity().pulseLocation(locationId, expires)
            }
            if (payloadType === Protocol.type_updateAudio) {
                const { volume } = payload

                this.clientGameLogic.audio.setVolume(volume)
            }
            if (payloadType === Protocol.type_sendBuildingTilePreview) {
                if (payload.committed !== undefined) {
                    this.clientGameLogic.playerController.buildingCommitResult(payload.roughCoordinate)
                } else {
                    this.clientGameLogic.playerController.updateTerrainPointer(
                        payload.deltas, payload.roughCoordinate, payload.tooFar, payload.illegal, payload.previewRevision, payload.itemId,
                    )
                }
            }
            if (payloadType === Protocol.type_sharedBuildingPreview) {
                if (payload.roomId === this.clientGameLogic.playerEntity?.roomId) {
                    this.clientGameLogic.playerController.updateSharedBuildingPreview(payload.entityId, payload.tiles)
                }
            }
        }
    }

    emitPlayerEvent = (playerEvent: PlayerEvent) => {
        this.send(playerEvent, Protocol.type_playerEvent)
    }

    // `location` is the fine world coordinate the player chose for orders that need a place as well
    // as an order -- "Hold Position" is the one that does. Sent as two flat numbers rather than a
    // nested object: this context is spread straight into a MARKER_EVENT and read by @when
    // conditions, and flat scalars are what every other field on this channel already is.
    // A name typed into the name prompt, for the entity it was opened over. The server hands it to
    // that entity's own script (which is what decides whether this player may name it) and cleans
    // the string on the way in -- the cap here only keeps the field honest.
    sendEntityName = (targetEntityId: string, name: string) => {
        this.emitPlayerEvent({
            playerEventType: "RENAME_ENTITY",
            context: {
                targetEntityId,
                name,
            },
        })
    }

    sendCommandWheelSelection = (command: string, npcType: string, targetEntityId?: string, location?: Coordinate) => {
        this.emitPlayerEvent({
            playerEventType: "SQUAD_COMMAND",
            context: {
                command,
                npcType,
                source: "command_wheel",
                // Present only for the per-entity wheel; the server scopes the MARKER_EVENT to this
                // NPC when set, otherwise the command broadcasts to the whole role.
                targetEntityId,
                ...(location ? { locationX: Math.round(location.x), locationY: Math.round(location.y) } : {}),
            },
        })
    }

    // The player's answer to the first-run tutorial invitation: "accept" or "skip", and whether
    // they clicked it or let the countdown run out. Rides the generic player-event channel, which
    // the server turns into a room-wide MARKER_EVENT that ember-tutorial.js listens for.
    sendEmberTutorialCommand = (command: string, reason: string = "click") => {
        this.emitPlayerEvent({
            playerEventType: "EMBER_TUTORIAL_COMMAND",
            context: {
                command,
                reason,
            },
        })
    }

    // The player pressed "Next" on a held speech bubble (see AvatarHud.spoke), so the speaker may go
    // on. Rides the generic player-event channel, which the server turns into a room-wide
    // MARKER_EVENT; a script that paces a conversation off these (ember-tutorial.js) then speaks at
    // the player's reading speed rather than its own clock.
    sendSpeechDismissed = (speakerEntityId: EntityId, holdId?: string) => {
        this.emitPlayerEvent({
            playerEventType: "SPEECH_DISMISSED",
            context: {
                speakerEntityId,
                holdId,
            },
        })
    }

    // A button on the ember end-of-run summary panel ("retry"). Rides the generic player-event
    // channel, which the server turns into a room-wide MARKER_EVENT the ember controller listens for.
    sendEmberRunCommand = (command: string) => {
        this.emitPlayerEvent({
            playerEventType: "EMBER_RUN_COMMAND",
            context: {
                command,
            },
        })
    }

    // The invasion handshake's four presses, all on the generic player-event channel (which the
    // server turns into a room-wide MARKER_EVENT) exactly like sendEmberRunCommand -- so none of
    // them needs a protocol number of its own:
    //   seek    -- "Resurrect as Undead!" on the end-of-run panel. A nudge for a fresher offer; the
    //              picker does NOT wait on it, because the client already holds the list.
    //   join    -- a card was picked. `entityId` is the DEFENDING player, which is how a pick names
    //              the camp it means (a room is not addressable from a client).
    //   cancel  -- the picker was dismissed.
    //   release -- "Let go" while wearing one of another camp's attackers.
    sendEmberInvasionCommand = (command: string, entityId?: EntityId) => {
        this.emitPlayerEvent({
            playerEventType: "EMBER_INVASION_COMMAND",
            context: {
                command,
                entityId,
            },
        })
    }

    startAttack = (entityId: EntityId) => {
        this.send(
            {
                entityId,
            },
            Protocol.type_startAttack,
        )
    }

    playBuildSwing = (roomId: string) => {
        this.send({ roomId }, Protocol.type_playAttackAnimation)
    }

    inventoryAction = (entityId: EntityId, itemId: string, action: InventoryAction, mapRoughCoordinates?: Coordinate) => {
        this.send(
            {
                entityId,
                itemId,
                action,
                ...(mapRoughCoordinates ? { mapRoughCoordinates } : {}),
            },
            Protocol.type_inventoryAction,
        )
    }

    activateWorldPoint = (roomFinePoint: Coordinate, options?: BuildingPointOptions) => {
        this.send(
            {
                roomFinePoint,
                options,
            },
            Protocol.type_activateWorldPoint,
        )
    }

    commitWorldEdits = (roughCoordinate?: Coordinate, pending = false) => {
        this.send(
            {
                roughCoordinate,
                pending,
            },
            Protocol.type_commitWorldEdits,
        )
    }

    commitWorldEditGroup = (roughCoordinates: Coordinate[], itemId: string) => {
        if (!roughCoordinates.length) return
        this.send({
            roughCoordinate: roughCoordinates[0], roughCoordinates, itemId, pending: true,
        }, Protocol.type_commitWorldEdits)
    }

    getBuildingTilePreview = (roughCoordinate: Coordinate, pendingCoordinates: Coordinate[] = [], previewRevision?: number, itemId?: string) => {
        this.send(
            {
                roughCoordinate,
                pendingCoordinates,
                previewRevision,
                itemId,
            },
            Protocol.type_getBuildingTilePreview,
        )
    }

    shareBuildingPreview = (roomId: string, itemId: string, tiles: { coordinate: Coordinate; itemId?: string; elapsedMs?: number }[]) => {
        this.send({ roomId, itemId, tiles }, Protocol.type_shareBuildingPreview)
    }

    requestEntityDetail = (entityIds: EntityId[]) => {
        this.send(
            {
                entityIds,
            },
            Protocol.type_requestEntityDetail,
        )
    }

    // Follow-cam: ask the server to sync this entity + its neighbors in full detail every tick
    // (pass null to stop). Drives the picture-in-picture view opened from the ember roster.
    // Ask the server to pull another online player into this player's ember camp. Nothing is checked
    // here: whether the invite is allowed, and where the guest lands, are the server's to decide (see
    // the EMBER_PLAYERS campaign).
    // The SpriteLab room's panel: make, spawn, leave, or just ask for the latest status. The server
    // checks room membership itself (see SpriteLabRoomService.request).
    sendSpriteLabRequest = (request: SpriteLabRequest) => {
        this.send(request, Protocol.type_spriteLabRequest)
    }

    // Also runs from socket teardown, where a renderer (or a test's stand-in) may have no GUI.
    private updateSpriteLab = (state: SpriteLabState | null, message?: string) =>
        this.clientGameLogic.clientRenderer?.gui?.()?.updateSpriteLab?.(state, message)

    inviteEmberPlayer = (entityId: EntityId, playMode: EmberPlayMode = "pvp") => {
        this.send({ entityId, playMode }, Protocol.type_inviteEmberPlayer)
    }

    // The invited player's answer to an invitation. Only an accepted one moves them, and it is the
    // server that moves them -- see the EMBER_PLAYERS campaign.
    setEmberPlayMode = (entityId: EntityId, playMode: EmberPlayMode) => {
        this.send({ entityId, playMode }, Protocol.type_setEmberPlayMode)
    }

    kickEmberPlayer = (entityId: EntityId) => {
        this.send({ entityId }, Protocol.type_kickEmberPlayer)
    }

    answerEmberInvite = (entityId: EntityId, accepted: boolean) => {
        this.send({ entityId, accepted }, Protocol.type_answerEmberInvite)
    }

    setFocusEntity = (entityId: EntityId | null) => {
        this.send({ entityId }, Protocol.type_setFocusEntity)
    }

    requestWorldMap = () => {
        this.send({}, Protocol.type_requestWorldMap)
    }

    worldMapClicked = (coordinate: Coordinate) => {
        this.send({ coordinate }, Protocol.type_worldMapClicked)
    }

    requestVisibility = () => {
        this.send({}, Protocol.type_requestVisibility)
    }

    itemPickupAction = () => {
        this.send({}, Protocol.type_requestItemPickup)
    }

    saveAppearance = (appearance: Appearance, inventory?: Inventory) => {
        this.send(
            {
                appearance,
                inventory,
            },
            Protocol.type_saveAppearance,
        )
    }

    finalizeCharacter = () => {
        this.send({}, Protocol.type_finalizeCharacter)
    }

    requestPlayerLocations = () => {
        this.send({}, Protocol.type_requestPlayerLocations)
    }

    onDialogClose = () => {
        this.emitPlayerEvent({
            playerEventType: "DIALOG_CLOSE",
            context: {},
        })
    }

    entityTileStrike = (location: Coordinate) => {
        const player = this.clientGameLogic.playerEntity
        if (player) this.clientGameLogic.clientRenderer.map().predictTreeStrike?.(location, player)
        this.send(
            {
                location,
            },
            Protocol.type_entityTileStrikeAt,
        )
    }

    // Shadow performance debugging methods
    getShadowPerformanceMetrics = () => {
        return this.clientGameLogic.getShadowPerformanceMetrics()
    }

    forceShadowTextureUpdate = () => {
        this.clientGameLogic.forceShadowTextureUpdate()
    }
}
